// CategorizeModal — review & fix transaction categories with hierarchical
// prime / subcategory system.

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  createSubcategory,
  deleteSubcategory,
  renameCategory,
  fetchLastMerge,
  undoMerge,
  fetchCategoriesHierarchy,
  fetchCategorizeGroupsV2,
  fetchLearnedRules,
  learnCategory,
  type CategorizeGroup,
  type LearnedRule,
  type Subcategory,
} from '../api';
import { PRIME_CATEGORIES, BUILTIN_SUBCATEGORIES, smartSearch } from '../utils/categorization';
import type { PrimeCategory, Subcategory as SubcategoryType } from '../types';

const fmt = (n: number): string =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n));

// ============================================================================
// SVG Icons
// ============================================================================

function PrimeIcon({ icon, color, size = 22 }: { icon: string; color: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon }}
    />
  );
}

// ============================================================================
// Component
// ============================================================================

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  /** Open the transaction list filtered to a specific category. */
  onViewTransactions?: (category: string) => void;
}

export function CategorizeModal({ open, onClose, onChanged, onViewTransactions }: Props) {
  const [primeCategories, setPrimeCategories] = useState<PrimeCategory[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [groups, setGroups] = useState<CategorizeGroup[]>([]);
  const [rules, setRules] = useState<LearnedRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyMerchant, setBusyMerchant] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selectedPrime, setSelectedPrime] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllMerchants, setShowAllMerchants] = useState(true);
  const [filterUncategorized, setFilterUncategorized] = useState(false);
  const [addingToPrime, setAddingToPrime] = useState<string | null>(null);
  const [newSubName, setNewSubName] = useState('');
  const [addingSub, setAddingSub] = useState(false);
  const [hoveredSub, setHoveredSub] = useState<string | null>(null);
  const [mergingSub, setMergingSub] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState<string>('');
  const [busyMerge, setBusyMerge] = useState(false);
  const [lastMerge, setLastMerge] = useState<{ from: string; to: string; count: number } | null>(null);
  const [busyUndo, setBusyUndo] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);

  // Build subcategory → prime mapping for rule display
  const searchSubMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of subcategories) map[s.name] = s.parentId;
    return map;
  }, [subcategories]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [hierarchyRes, rulesRes, gRes] = await Promise.all([
        fetchCategoriesHierarchy(),
        fetchLearnedRules(),
        fetchCategorizeGroupsV2(selectedPrime ?? undefined, searchQuery || undefined),
      ]);
      setPrimeCategories(hierarchyRes.primeCategories);
      setSubcategories(hierarchyRes.subcategories);
      setRules(rulesRes.rules);
      setGroups(gRes.groups);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [selectedPrime, searchQuery]);

  useEffect(() => {
    if (open) {
      load();
      fetchLastMerge()
        .then((r) => setLastMerge(r.merge))
        .catch(() => setLastMerge(null));
    }
  }, [open, load]);

  // Auto-select first prime on open
  useEffect(() => {
    if (open && primeCategories.length > 0 && !selectedPrime) {
      setSelectedPrime(primeCategories[0].id);
    }
  }, [open, primeCategories, selectedPrime]);

  // Auto-focus search on open
  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset adding state when prime changes
  useEffect(() => {
    setAddingToPrime(null);
    setNewSubName('');
  }, [selectedPrime]);

  const selectedPrimeData = useMemo(
    () => primeCategories.find(p => p.id === selectedPrime),
    [primeCategories, selectedPrime],
  );

  const subcatsForSelected = useMemo(() => {
    const builtins = (BUILTIN_SUBCATEGORIES[selectedPrime ?? ''] || []).map(s => ({
      id: s.id,
      name: s.name,
      parentId: selectedPrime ?? '',
      isCustom: false,
    })) as Subcategory[];
    const customs = subcategories.filter(s => s.parentId === selectedPrime);
    return [...builtins, ...customs];
  }, [subcategories, selectedPrime]);

  const handleAssign = async (merchant: string, category: string) => {
    if (!category) return;
    setBusyMerchant(merchant);
    setMessage(null);
    setError(null);
    try {
      const res = await learnCategory(merchant, category);
      setMessage(
        `"${merchant}" → ${category}${res.applied > 0 ? ` · applied to ${res.applied} transaction${res.applied === 1 ? '' : 's'}` : ''}`,
      );
      load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the category rule.');
    } finally {
      setBusyMerchant(null);
    }
  };

  const handleAddSubcategory = async () => {
    const name = newSubName.trim();
    if (!name || !addingToPrime) return;
    setAddingSub(true);
    setError(null);
    try {
      await createSubcategory(addingToPrime, name);
      setMessage(`✅ Created subcategory "${name}" under ${addingToPrime}.`);
      setNewSubName('');
      setAddingToPrime(null);
      load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create subcategory.');
    } finally {
      setAddingSub(false);
    }
  };

  const handleDeleteSubcategory = async (sub: Subcategory) => {
    if (!sub.isCustom) return;
    setError(null);
    try {
      await deleteSubcategory(sub.id);
      setMessage(`🗑️ Removed subcategory "${sub.name}".`);
      load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete subcategory.');
    }
  };

  // All distinct category names actually present in the transactions
  // (Firefly categories) + the defined subcategories. Used as merge targets so
  // the dropdown always matches what filtering will find.
  const allCategoryNames = useMemo(() => {
    const set = new Set<string>();
    for (const g of groups) if (g.current_category) set.add(g.current_category);
    for (const s of subcategories) set.add(s.name);
    for (const p of primeCategories) {
      for (const s of BUILTIN_SUBCATEGORIES[p.id] || []) set.add(s.name);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [groups, subcategories, primeCategories]);

  const handleMerge = async (from: string, to: string) => {
    if (!to || from === to) return;
    setBusyMerge(true);
    setError(null);
    try {
      const res = await renameCategory(from, to);
      if (!res.ok) {
        setError(res.error ?? 'Failed to merge categories.');
      } else {
        setMessage(
          `🔀 Merged "${from}" → "${to}" · ${res.updated} transaction${res.updated === 1 ? '' : 's'} moved` +
            (res.rulesChanged ? `, ${res.rulesChanged} rule${res.rulesChanged === 1 ? '' : 's'} updated` : ''),
        );
        setMergingSub(null);
        setMergeTarget('');
        setLastMerge({ from, to, count: res.updated });
        load();
        onChanged();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to merge categories.');
    } finally {
      setBusyMerge(false);
    }
  };

  const handleUndoMerge = async () => {
    if (!lastMerge) return;
    setBusyUndo(true);
    setError(null);
    try {
      const res = await undoMerge();
      if (!res.ok) {
        setError(res.error ?? 'Failed to undo merge.');
      } else {
        setMessage(
          `↩️ Reversed merge: ${res.restored} transaction${res.restored === 1 ? '' : 's'} moved back to "${res.from}"`,
        );
        setLastMerge(null);
        load();
        onChanged();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to undo merge.');
    } finally {
      setBusyUndo(false);
    }
  };

  // Smart search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    return smartSearch(searchQuery);
  }, [searchQuery]);

  const filteredGroups = useMemo(() => {
    if (filterUncategorized) {
      return groups.filter(g => !g.rule_applied && g.current_prime_category === 'others');
    }
    return groups;
  }, [groups, filterUncategorized]);

  // Subcategory color helper
  const subColor = (primeId: string) => {
    const prime = PRIME_CATEGORIES.find(p => p.id === primeId);
    if (!prime) return { bg: 'bg-slate-500/10', text: 'text-slate-300', border: 'border-slate-500/20' };
    return { bg: prime.bgClass, text: prime.textClass, border: prime.borderClass };
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="my-0 w-full max-w-3xl rounded-t-2xl border border-zinc-800 bg-[#0c0c0f] shadow-2xl shadow-black/60 sm:my-8 sm:rounded-2xl max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div className="flex items-center justify-between border-b border-zinc-800/80 px-6 pt-5 pb-4">
          <div>
            <h2 className="text-base font-bold tracking-tight text-zinc-100">Manage Categories</h2>
            <p className="mt-0.5 text-xs text-zinc-500">Organize transactions — add subcategories, assign merchants</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-700/60 text-zinc-400 transition hover:border-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-200"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ─── Messages ─── */}
        <div className="px-6 pt-3">
          {message && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-400">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {message}
            </div>
          )}
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/5 px-3 py-2 text-xs text-rose-400">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {lastMerge && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              <span>
                Last merge: “{lastMerge.from}” → “{lastMerge.to}” ({lastMerge.count})
              </span>
              <button
                onClick={() => void handleUndoMerge()}
                disabled={busyUndo}
                className="rounded-lg bg-amber-500 px-2.5 py-1 text-xs font-semibold text-black transition hover:bg-amber-400 disabled:opacity-50"
              >
                {busyUndo ? '…' : 'Undo (demerge)'}
              </button>
            </div>
          )}
        </div>

        {/* ─── Search ─── */}
        <div className="px-6 pt-1 pb-3">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              width="15" height="15" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search categories, merchants, keywords…"
              className="w-full rounded-xl border border-zinc-700/70 bg-zinc-900/80 py-2.5 pl-9 pr-4 text-sm text-zinc-200 outline-none transition placeholder:text-zinc-600 focus:border-zinc-600"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Search results dropdown */}
          {searchResults && searchResults.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-900/90 p-1.5">
              {searchResults.map((r, i) => {
                if ('parentId' in r && !('icon' in r)) {
                  const sc = r as SubcategoryType;
                  const prime = PRIME_CATEGORIES.find(p => p.id === sc.parentId);
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setSelectedPrime(sc.parentId);
                        setSearchQuery('');
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs text-zinc-300 transition hover:bg-zinc-800"
                    >
                      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: prime?.color }} />
                      {sc.name}
                      <span className="text-zinc-600">· {prime?.name}</span>
                    </button>
                  );
                }
                const prime = r as PrimeCategory;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      setSelectedPrime(prime.id);
                      setSearchQuery('');
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs text-zinc-300 transition hover:bg-zinc-800"
                  >
                    <PrimeIcon icon={prime.icon} color={prime.color} size={14} />
                    {prime.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── Prime Category Cards ─── */}
        <div className="px-6 pb-4">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {primeCategories.map((prime) => {
              const isActive = selectedPrime === prime.id;
              const subCount = (BUILTIN_SUBCATEGORIES[prime.id] || []).length +
                subcategories.filter(s => s.parentId === prime.id).length;

              return (
                <button
                  key={prime.id}
                  onClick={() => setSelectedPrime(prime.id)}
                  className={`group relative flex min-w-[130px] flex-col items-center gap-2 rounded-xl border px-4 py-3 transition-all duration-200 ${
                    isActive
                      ? 'border-zinc-600 bg-zinc-800/70 shadow-lg'
                      : 'border-zinc-800/60 bg-zinc-900/40 hover:border-zinc-700 hover:bg-zinc-800/40'
                  }`}
                  style={isActive ? { boxShadow: `0 0 24px -6px ${prime.color}22` } : {}}
                >
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
                    style={{ backgroundColor: isActive ? `${prime.color}18` : 'transparent' }}
                  >
                    <PrimeIcon icon={prime.icon} color={prime.color} size={22} />
                  </div>
                  <div className="text-center">
                    <p className={`text-xs font-semibold ${isActive ? 'text-zinc-100' : 'text-zinc-400 group-hover:text-zinc-300'}`}>
                      {prime.name}
                    </p>
                    <p className="text-[10px] text-zinc-600">{subCount} sub{subCount !== 1 ? 's' : ''}</p>
                  </div>
                  {isActive && (
                    <div className="absolute -bottom-px left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full" style={{ backgroundColor: prime.color }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Subcategories Section ─── */}
        {selectedPrimeData && (
          <div className="mx-6 mb-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PrimeIcon icon={selectedPrimeData.icon} color={selectedPrimeData.color} size={16} />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                  {selectedPrimeData.name} — Subcategories
                </h3>
              </div>
              {addingToPrime === selectedPrime ? (
                <div className="flex items-center gap-1.5">
                  <input
                    value={newSubName}
                    onChange={(e) => setNewSubName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddSubcategory()}
                    placeholder="Subcategory name…"
                    autoFocus
                    className="w-36 rounded-lg border border-zinc-700 bg-zinc-800 px-2.5 py-1.5 text-xs text-zinc-200 outline-none focus:border-zinc-500"
                  />
                  <button
                    onClick={handleAddSubcategory}
                    disabled={addingSub || !newSubName.trim()}
                    className="rounded-lg border border-zinc-700 bg-zinc-800 px-2.5 py-1.5 text-xs font-semibold text-zinc-300 transition hover:bg-zinc-700 disabled:opacity-40"
                  >
                    {addingSub ? '…' : 'Add'}
                  </button>
                  <button
                    onClick={() => { setAddingToPrime(null); setNewSubName(''); }}
                    className="rounded-lg px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-300"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingToPrime(selectedPrime)}
                  className="flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-xs font-semibold text-zinc-400 transition hover:border-zinc-700 hover:bg-zinc-800 hover:text-zinc-200"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add Subcategory
                </button>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {subcatsForSelected.map((sub) => {
                const colors = subColor(sub.parentId);
                const isHovered = hoveredSub === sub.id;
                return (
                  <span
                    key={sub.id}
                    className={`group inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-all ${
                      colors.bg + ' ' + colors.text + ' ' + (isHovered ? colors.border : 'border-transparent')
                    }`}
                    onMouseEnter={() => setHoveredSub(sub.id)}
                    onMouseLeave={() => setHoveredSub(null)}
                  >
                    <button
                      type="button"
                      onClick={() => onViewTransactions?.(sub.name)}
                      title="Click to filter transactions by this category"
                      className="font-medium hover:underline"
                    >
                      {sub.name}
                    </button>
                    {sub.isCustom && (
                      <button
                        onClick={() => handleDeleteSubcategory(sub)}
                        title="Remove subcategory"
                        className={`ml-0.5 rounded transition ${
                          isHovered ? 'text-zinc-400 hover:text-rose-400' : 'text-transparent'
                        }`}
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => { setMergingSub(sub.name); setMergeTarget(''); }}
                      title="Merge / delete this category into another"
                      className={`ml-0.5 rounded transition ${
                        isHovered ? 'text-zinc-400 hover:text-amber-400' : 'text-transparent'
                      }`}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 7h10M7 17h10M17 7l-3 5 3 5M7 7l3 5-3 5" />
                      </svg>
                    </button>
                  </span>
                );
              })}
              {subcatsForSelected.length === 0 && (
                <span className="text-xs text-zinc-600 italic">No subcategories yet</span>
              )}
            </div>

            {mergingSub && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <span className="text-[11px] font-semibold text-amber-300">
                  Merge “{mergingSub}” into:
                </span>
                <select
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  className="rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-zinc-200 outline-none focus:border-zinc-500"
                >
                  <option value="">Select target…</option>
                  {allCategoryNames.filter((n) => n !== mergingSub).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <button
                  onClick={() => void handleMerge(mergingSub, mergeTarget)}
                  disabled={busyMerge || !mergeTarget}
                  className="rounded-lg bg-amber-500 px-2.5 py-1 text-xs font-semibold text-black transition hover:bg-amber-400 disabled:opacity-40"
                >
                  {busyMerge ? '…' : 'Merge'}
                </button>
                <button
                  onClick={() => { setMergingSub(null); setMergeTarget(''); }}
                  className="rounded px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── Filter Tabs ─── */}
        <div className="mx-6 mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-900/40 p-0.5">
            <button
              onClick={() => setShowAllMerchants(true)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                showAllMerchants ? 'bg-zinc-800 text-zinc-200 shadow-sm' : 'text-zinc-500 hover:text-zinc-400'
              }`}
            >
              All Merchants
            </button>
            <button
              onClick={() => setShowAllMerchants(false)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                !showAllMerchants ? 'bg-zinc-800 text-zinc-200 shadow-sm' : 'text-zinc-500 hover:text-zinc-400'
              }`}
            >
              {selectedPrimeData?.name ?? 'This Category'}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={filterUncategorized}
                onChange={(e) => setFilterUncategorized(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 text-zinc-500 accent-zinc-500"
              />
              <span className="text-[11px] text-zinc-500">Uncategorized only</span>
            </label>
            <span className="text-[11px] text-zinc-600">
              {filteredGroups.length} merchant{filteredGroups.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* ─── Merchant List ─── */}
        <div className="mx-6 mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-5 w-5 animate-spin rounded-full border border-zinc-700 border-t-zinc-400" />
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-zinc-600">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2 opacity-40">
                <path d="M3 3h18v18H3zM21 9H3M9 21V9" />
              </svg>
              <p className="text-xs">
                {filterUncategorized ? 'No uncategorized transactions.' : 'No merchants found.'}
              </p>
            </div>
          ) : (
            <div className="max-h-[40vh] space-y-1 overflow-y-auto pr-1 scrollbar-thin">
              {filteredGroups.map((g) => (
                <div
                  key={g.merchant}
                  className="group flex flex-wrap items-center gap-2.5 rounded-xl border border-zinc-800/60 bg-zinc-900/20 px-3.5 py-2.5 transition hover:border-zinc-700/80 hover:bg-zinc-800/30"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-200" title={g.merchant}>
                      {g.merchant}
                      {g.current_type && (
                        <span
                          className={`ml-2 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-bold ${
                            g.current_type === 'credit'
                              ? 'bg-emerald-500/15 text-emerald-300'
                              : g.current_type === 'debit'
                                ? 'bg-rose-500/15 text-rose-300'
                                : 'bg-zinc-500/15 text-zinc-300'
                          }`}
                          title="Credit / Debit"
                        >
                          {g.current_type === 'credit' ? 'CR' : g.current_type === 'debit' ? 'DB' : 'CR/DB'}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-zinc-600">
                      {g.count} txn · ₹{fmt(g.total)} · {new Date(g.last_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      {g.rule_applied && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-zinc-500">
                          <span className="inline-block h-1 w-1 rounded-full bg-zinc-600" />
                          rule
                        </span>
                      )}
                    </p>
                  </div>

                  {g.current_prime_category && (
                    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium ${
                      (subColor(g.current_prime_category).bg + ' ' + subColor(g.current_prime_category).text + ' ' + subColor(g.current_prime_category).border)
                    }`}>
                      {g.current_category}
                    </span>
                  )}

                  <select
                    value=""
                    onChange={(e) => e.target.value && handleAssign(g.merchant, e.target.value)}
                    disabled={busyMerchant === g.merchant}
                    className="rounded-lg border border-zinc-700/60 bg-zinc-800/60 px-2.5 py-1.5 text-xs text-zinc-300 outline-none transition focus:border-zinc-600 disabled:opacity-40"
                  >
                    <option value="">{busyMerchant === g.merchant ? 'Saving…' : 'Change →'}</option>
                    {PRIME_CATEGORIES.map(prime => {
                      const subs = [...(BUILTIN_SUBCATEGORIES[prime.id] || []), ...subcategories.filter(s => s.parentId === prime.id && s.isCustom)];
                      return subs.map(sub => (
                        <option key={sub.id} value={sub.name}>
                          {prime.name} › {sub.name}
                        </option>
                      ));
                    })}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── Rules Summary ─── */}
        {rules.length > 0 && (
          <div className="mx-6 mb-6">
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
              Active Rules ({rules.length})
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {rules.slice(0, 20).map((r) => {
                const primeId = searchSubMap[r.category] || 'others';
                const colors = subColor(primeId);
                return (
                  <span
                    key={r.merchant}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] ${colors.bg + ' ' + colors.text + ' ' + colors.border}`}
                  >
                    <span className="text-zinc-500">{r.merchant}</span>
                    <span className="text-zinc-600">→</span>
                    <span className="font-medium">{r.category}</span>
                  </span>
                );
              })}
              {rules.length > 20 && (
                <span className="text-[11px] text-zinc-600">+{rules.length - 20} more</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
